import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { useAuth } from './AuthContext';

/**
 * App-wide default "rows per page" preference. Read once at app boot, used as
 * the initial `limit` on every paginated list view. Admin can change it via
 * Settings → "List Page Size".
 *
 * Fallback to 100 if the request fails (e.g. unauthenticated / offline).
 */
const DEFAULT = 100;
const PageSizeContext = createContext({ pageSize: DEFAULT, setPageSize: () => {}, refresh: () => {} });

export const PageSizeProvider = ({ children }) => {
  const [pageSize, setPageSizeState] = useState(DEFAULT);
  const { isAuthenticated } = useAuth();

  const refresh = useCallback(async () => {
    // Skip the API call when no token is present — calling an authenticated
    // endpoint on the login page would trigger the response interceptor's
    // 401 handler which redirects to /login, causing a remount/reload loop.
    if (!localStorage.getItem('access_token')) return;
    try {
      const res = await api.get('/settings/page-size');
      if (res.data.success) setPageSizeState(res.data.data.value);
    } catch { /* keep default */ }
  }, []);

  // Re-fetch on auth state transition (e.g. after login completes). The
  // unauthenticated path is a no-op so the initial render on /login is safe.
  useEffect(() => { refresh(); }, [refresh, isAuthenticated]);

  const setPageSize = useCallback(async (value) => {
    setPageSizeState(value);   // optimistic
    try {
      await api.put('/settings/page-size', { value });
    } catch { /* swallow — UI already updated */ }
  }, []);

  return (
    <PageSizeContext.Provider value={{ pageSize, setPageSize, refresh }}>
      {children}
    </PageSizeContext.Provider>
  );
};

export const usePageSize = () => useContext(PageSizeContext);
